import { Component, ViewChild, Injector, ElementRef, Input, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { SupportComponentBase } from '../component-base.component';
import { firstValueFrom } from 'rxjs';
import { SupportMessageDto, SupportMessageInput, SupportTicketDto } from '@shared/service-proxies/general-proxies';
import { MazarsButtonComponent } from '@app/modules/mazars-common/components/mazars-button/mazars-button.component';
import { MazarsInputTextAreaComponent } from '@app/modules/mazars-common/components/mazars-input-textarea/mazars-input-textarea.component';
import { SupportMessagesService } from '../support.services';
import { HubConnection } from '@microsoft/signalr';
import { IUserNotification } from '@shared/service-proxies/service-proxies';
import { DatePipe, NgIf } from '@angular/common';
import { MazarsInputTextAreaComponent as MazarsInputTextAreaComponent_1 } from '../../modules/mazars-common/components/mazars-input-textarea/mazars-input-textarea.component';
import { MazarsButtonComponent as MazarsButtonComponent_1 } from '../../modules/mazars-common/components/mazars-button/mazars-button.component';

@Component({
    selector: 'support-messages',
    templateUrl: './support-messages.component.html',
    styleUrl: './support-messages.component.css',
    imports: [
        NgIf,
        MazarsInputTextAreaComponent_1,
        MazarsButtonComponent_1,
    ],
})
export class SupportMessagesComponent extends SupportComponentBase implements AfterViewInit, OnDestroy {
    @ViewChild('msgWrite', { static: true }) messageInput: MazarsInputTextAreaComponent;
    @ViewChild('msgSend', { static: true }) messageButton: MazarsButtonComponent;
    @ViewChild('msgContainer', { static: true }) messageContainer: ElementRef;

    @Input({ required: true }) tenantId: number;
    @Input({ required: true }) ticket: SupportTicketDto;
    @Input({ required: true }) isUser: boolean;
    @Input() token?: string;

    connection?: HubConnection;
    messages: SupportMessageDto[];

    public constructor(
        injector: Injector,
        private supportMessageService: SupportMessagesService,
        protected datePipe: DatePipe,
        private changeDetector: ChangeDetectorRef,
    ) {
        super(injector);
    }

    async ngAfterViewInit() {
        this.supportMessageService.accessToken = this.token;

        this.subscribeToEvent('abp.notifications.received', (event: IUserNotification) => {
            const name = event.notification.notificationName;
            if (name.startsWith('App.SupportTicketMessage')) {
                this.loadMessages();
            }
        });

        try {
            this.connection = await abp.signalr.startConnection(abp.appPath + 'signalr-support-messages', (connection: HubConnection) => {
                connection.on('update', () => this.loadMessages());
            });

            await this.connection.invoke('subscribe', this.ticket.id, this.tenantId);
        } catch (e) {
            console.error('Unable to reach messages hub!');
            throw e;
        }

        this.loadMessages();
    }

    ngOnDestroy(): void {
        this.connection?.stop();
    }

    loadMessages() {
        this.supportMessageService.list(this.ticket.id, this.tenantId ?? undefined, !this.isUser).subscribe((msgs) => {
            if (!this.messages) {
                this.messages = msgs.reverse();
            } else {
                this.messages.splice(0, this.messages.length, ...msgs.reverse());
            }

            // this is necessary
            this.changeDetector.detectChanges();
        });
    }

    async writeMessage() {
        this.messageInput.isDisabled = true;
        this.messageButton.busy = true;

        try {
            const text = this.messageInput.value;

            await firstValueFrom(this.supportMessageService.write(this.ticket.id, this.tenantId ?? undefined, new SupportMessageInput({
                text: text,
                fromAgent: !this.isUser,
            })));

            this.messageInput.value = '';
        } finally {
            this.messageInput.isDisabled = false;
            this.messageButton.busy = false;
        }
    }

    isMine(msg: SupportMessageDto): boolean {
        if (this.isUser) {
            return !msg.isAgent;
        } else {
            return msg.isAgent;
        }
    }
}
